export const NOTE_NAMES = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'];

// Rootless voicings: root and 5th omitted, keeping 3rd, 7th, and extensions
const ROOTLESS_INTERVALS: Record<string, number[]> = {
  maj7:    [4, 11],
  maj9:    [4, 11, 14],
  min7:    [3, 10],
  min9:    [3, 10, 14],
  min11:   [3, 10, 14, 17],
  dom7:    [4, 10],
  dom9:    [4, 10, 14],
  halfdim: [3, 6, 10],
};

const TYPE_LABELS: Record<string, string> = {
  maj7: 'maj7', maj9: 'maj9',
  min7: 'min7', min9: 'min9', min11: 'min11',
  dom7: '7',    dom9: '9',
  halfdim: 'ø7',
};

// Full chord tones (including root + 5th) used for melody note pool
const MELODY_INTERVALS: Record<string, number[]> = {
  maj7:    [0, 4, 7, 11, 14],
  maj9:    [0, 4, 7, 11, 14],
  min7:    [0, 3, 7, 10, 14],
  min9:    [0, 3, 7, 10, 14],
  min11:   [0, 3, 7, 10, 14, 17],
  dom7:    [0, 4, 7, 10, 14],
  dom9:    [0, 4, 7, 10, 14],
  halfdim: [0, 3, 6, 10, 14],
};

export type ChordType = keyof typeof ROOTLESS_INTERVALS;

export interface ChordDegree {
  semitones: number;
  type: ChordType;
}

export interface ChordContext {
  keyRootMidi: number;
  degree: ChordDegree;
}

export const PROGRESSION_LABELS = [
  'ii–V–I–I',
  'I–vi–ii–V',
  'i–♭VII–♭VI–V',
  'I–IV–iii–♭VII',
  'I–♭VII–IV–I',
  'I–iii–IV–V',
  'I–V–vi–IV',
  'I–iii–vi–IV',
];

// 4-chord lofi/neo-soul progressions, each chord lasts 2 bars
export const PROGRESSIONS: ChordDegree[][] = [
  // ii–V–I–I (jazz standard)
  [
    { semitones: 2,  type: 'min9' },
    { semitones: 7,  type: 'dom9' },
    { semitones: 0,  type: 'maj9' },
    { semitones: 0,  type: 'maj9' },
  ],
  // I–vi–ii–V (classic loop)
  [
    { semitones: 0,  type: 'maj9' },
    { semitones: 9,  type: 'min7' },
    { semitones: 2,  type: 'min9' },
    { semitones: 7,  type: 'dom9' },
  ],
  // i–♭VII–♭VI–V (minor neo-soul)
  [
    { semitones: 0,  type: 'min9' },
    { semitones: 10, type: 'dom7' },
    { semitones: 8,  type: 'maj7' },
    { semitones: 7,  type: 'dom7' },
  ],
  // I–IV–iii–♭VII (modal borrowed)
  [
    { semitones: 0,  type: 'maj9' },
    { semitones: 5,  type: 'maj9' },
    { semitones: 4,  type: 'min7' },
    { semitones: 10, type: 'dom7' },
  ],
  // I–♭VII–IV–I (major plagal loop)
  [
    { semitones: 0,  type: 'maj9' },
    { semitones: 10, type: 'dom7' },
    { semitones: 5,  type: 'maj9' },
    { semitones: 0,  type: 'maj9' },
  ],
  // I–iii–IV–V (bright ascending major)
  [
    { semitones: 0,  type: 'maj9' },
    { semitones: 4,  type: 'min7' },
    { semitones: 5,  type: 'maj9' },
    { semitones: 7,  type: 'dom9' },
  ],
  // I–V–vi–IV (pop loop)
  [
    { semitones: 0,  type: 'maj9' },
    { semitones: 7,  type: 'dom9' },
    { semitones: 9,  type: 'min7' },
    { semitones: 5,  type: 'maj9' },
  ],
  // I–iii–vi–IV (neo-soul bright loop)
  [
    { semitones: 0,  type: 'maj9' },
    { semitones: 4,  type: 'min7' },
    { semitones: 9,  type: 'min9' },
    { semitones: 5,  type: 'maj9' },
  ],
];

function midiToNoteName(midi: number): string {
  const octave = Math.floor(midi / 12) - 1;
  return `${NOTE_NAMES[midi % 12]}${octave}`;
}

export interface PhraseNote {
  degreeIndex: number; // index into MELODY_INTERVALS[type]
  offset8n: number;    // 8th-note offset from chord start (0–15 for a 2-bar window)
  dur: string;         // Tone.js duration string
}

export type Phrase = PhraseNote[];

// Each phrase is a short melodic idea expressed as chord-degree indices so it
// adapts automatically to any chord type and key.
export const MELODY_PHRASE_NAMES = [
  "ascent", "high arch", "question", "descent", "rocking", "sparse high",
  "syncopated", "late entry", "stepwise fall", "pickup", "delayed rock", "oscillate",
] as const;

export const MELODY_PHRASES: Phrase[] = [
  // Stepwise ascent — root → 3rd → 5th, then 7th in bar 2
  [
    { degreeIndex: 0, offset8n: 0,  dur: "4n" },
    { degreeIndex: 1, offset8n: 3,  dur: "8n" },
    { degreeIndex: 2, offset8n: 5,  dur: "4n" },
    { degreeIndex: 3, offset8n: 11, dur: "4n" },
  ],
  // High arch — 9th down to 3rd
  [
    { degreeIndex: 4, offset8n: 1,  dur: "8n" },
    { degreeIndex: 3, offset8n: 2,  dur: "4n" },
    { degreeIndex: 2, offset8n: 6,  dur: "8n" },
    { degreeIndex: 1, offset8n: 9,  dur: "2n" },
  ],
  // Question — 5th up to 9th, rest, answer on root
  [
    { degreeIndex: 2, offset8n: 0,  dur: "4n" },
    { degreeIndex: 3, offset8n: 3,  dur: "8n" },
    { degreeIndex: 4, offset8n: 5,  dur: "8n" },
    { degreeIndex: 0, offset8n: 10, dur: "4n" },
  ],
  // Descent — 9th → 5th → 3rd → root (long)
  [
    { degreeIndex: 4, offset8n: 0,  dur: "4n" },
    { degreeIndex: 2, offset8n: 3,  dur: "8n" },
    { degreeIndex: 1, offset8n: 5,  dur: "4n" },
    { degreeIndex: 0, offset8n: 8,  dur: "2n" },
  ],
  // Rocking — oscillates between 3rd and 5th across both bars
  [
    { degreeIndex: 1, offset8n: 1,  dur: "8n" },
    { degreeIndex: 2, offset8n: 4,  dur: "4n" },
    { degreeIndex: 1, offset8n: 7,  dur: "8n" },
    { degreeIndex: 2, offset8n: 10, dur: "4n" },
    { degreeIndex: 1, offset8n: 13, dur: "8n" },
  ],
  // Sparse high — 7th → 9th → 7th with lots of space
  [
    { degreeIndex: 3, offset8n: 2,  dur: "4n" },
    { degreeIndex: 4, offset8n: 6,  dur: "4n" },
    { degreeIndex: 3, offset8n: 12, dur: "2n" },
  ],
  // Syncopated — hits the off-beats for a groovy, pushed feel
  [
    { degreeIndex: 0, offset8n: 1,  dur: "8n" },
    { degreeIndex: 1, offset8n: 3,  dur: "8n" },
    { degreeIndex: 2, offset8n: 6,  dur: "8n" },
    { degreeIndex: 3, offset8n: 9,  dur: "4n" },
    { degreeIndex: 2, offset8n: 13, dur: "8n" },
  ],
  // Late entry — silent bar 1, phrase enters on bar 2
  [
    { degreeIndex: 3, offset8n: 8,  dur: "4n" },
    { degreeIndex: 4, offset8n: 11, dur: "8n" },
    { degreeIndex: 2, offset8n: 13, dur: "4n" },
  ],
  // Stepwise fall — smooth descent through all 5 chord tones
  [
    { degreeIndex: 4, offset8n: 2,  dur: "8n" },
    { degreeIndex: 3, offset8n: 4,  dur: "8n" },
    { degreeIndex: 2, offset8n: 6,  dur: "8n" },
    { degreeIndex: 1, offset8n: 8,  dur: "8n" },
    { degreeIndex: 0, offset8n: 10, dur: "4n" },
  ],
  // Pickup → land — anticipation notes rush into a long downbeat on bar 2
  [
    { degreeIndex: 0, offset8n: 0,  dur: "4n" },
    { degreeIndex: 4, offset8n: 6,  dur: "8n" },
    { degreeIndex: 3, offset8n: 7,  dur: "8n" },
    { degreeIndex: 1, offset8n: 8,  dur: "2n" },
  ],
  // Delayed rock — starts on beat 2, so bar 2 feels more alive
  [
    { degreeIndex: 1, offset8n: 2,  dur: "8n" },
    { degreeIndex: 2, offset8n: 4,  dur: "4n" },
    { degreeIndex: 3, offset8n: 9,  dur: "8n" },
    { degreeIndex: 2, offset8n: 11, dur: "8n" },
    { degreeIndex: 1, offset8n: 13, dur: "4n" },
  ],
  // Oscillate — rocks between 5th and 7th across the window
  [
    { degreeIndex: 2, offset8n: 0,  dur: "8n" },
    { degreeIndex: 3, offset8n: 3,  dur: "8n" },
    { degreeIndex: 2, offset8n: 5,  dur: "8n" },
    { degreeIndex: 3, offset8n: 8,  dur: "4n" },
    { degreeIndex: 2, offset8n: 12, dur: "4n" },
  ],
];

export function getPhraseMelodyNote(
  degreeIndex: number,
  rootMidi: number,
  chordType: ChordType,
  midiMin: number,
  midiMax: number,
): string | null {
  const intervals = MELODY_INTERVALS[chordType];
  const interval = intervals[Math.min(degreeIndex, intervals.length - 1)];
  const targetPc = (rootMidi + interval) % 12;
  for (let midi = midiMin; midi <= midiMax; midi++) {
    if (midi % 12 === targetPc) return midiToNoteName(midi);
  }
  return null;
}

export interface VoicingResult {
  name: string;
  notes: string[];
}

export function buildVoicing(keyRootMidi: number, degree: ChordDegree, registerOffset = 0): VoicingResult {
  const rootMidi = keyRootMidi + degree.semitones;
  const intervals = ROOTLESS_INTERVALS[degree.type];

  // Anchor the base root in register-shifted A2–A3 (MIDI 45–57 + offset)
  const anchorLow  = 45 + registerOffset;
  const anchorHigh = 57 + registerOffset;
  const cap        = 72 + registerOffset;
  let baseRoot = rootMidi;
  while (baseRoot > anchorHigh) baseRoot -= 12;
  while (baseRoot < anchorLow)  baseRoot += 12;

  // Stack intervals upward from baseRoot, keeping notes ascending
  const midiNotes: number[] = [];
  let prev = baseRoot;
  for (const interval of intervals) {
    let midi = baseRoot + interval;
    while (midi < prev) midi += 12;
    while (midi > cap) midi -= 12;
    midiNotes.push(midi);
    prev = midi;
  }

  return {
    name: `${NOTE_NAMES[rootMidi % 12]}${TYPE_LABELS[degree.type]}`,
    notes: midiNotes.map(midiToNoteName),
  };
}

// Returns all chord tones in the given MIDI register (default F4–C6, MIDI 65–84)
export function getMelodyPool(
  keyRootMidi: number,
  degree: ChordDegree,
  midiMin = 65,
  midiMax = 84,
): string[] {
  const rootMidi = keyRootMidi + degree.semitones;
  const pitchClasses = new Set(
    MELODY_INTERVALS[degree.type].map((i) => (rootMidi + i) % 12)
  );
  const notes: string[] = [];
  for (let midi = midiMin; midi <= midiMax; midi++) {
    if (pitchClasses.has(midi % 12)) notes.push(midiToNoteName(midi));
  }
  return notes;
}

export function randomProgressionIndex(excluding?: number): number {
  if (PROGRESSIONS.length <= 1) return 0;
  let idx: number;
  do { idx = Math.floor(Math.random() * PROGRESSIONS.length); } while (idx === excluding);
  return idx;
}

export function randomProgression(): ChordDegree[] {
  return PROGRESSIONS[randomProgressionIndex()];
}

export function randomKeyMidi(): number {
  // All 12 keys, root anchored to octave 3 (C3=48 … B3=59)
  return 48 + Math.floor(Math.random() * 12);
}
